// src/pages/Internship/InternshipDraftManagement.jsx

import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  TablePagination,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ApiService from "../../_services/ApiService";
import { CardSkeletonCollab } from "../../components/reusableComponents/SkeletonCard";
import NoResults from "../../components/reusableComponents/NoResults";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useMediaQuery, useTheme } from "@mui/material";
import Tablecomponent from "../../components/reusableComponents/Tablecomponent";
import InternshipCard from "./internshipCard";
import bgimage from "../../img/no_image.png";

const InternshipDraftManagement = () => {
  const history = useHistory();
  const userData = useSelector((state) => state.userData.userData);

  const [internshipDrafts, setInternshipDrafts] = useState([]);
  const [loader, setLoader] = useState(false);

  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(""); // 🔹 new
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const stripHtml = (html) =>
    typeof html === "string" ? html.replace(/<[^>]+>/g, "") : "";

  // ---------- TABLE COLUMNS ----------
  const columns = [
    {
      headerName: "Brand",
      field: "BrandLogoURL",
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <img
            src={row?.BrandLogoURL || bgimage}
            alt="brand-logo"
            width={50}
            height={50}
            style={{
              borderRadius: "50%",
              objectFit: "contain",
              backgroundColor: "#F5F5F5",
            }}
          />
        </Box>
      ),
    },
    {
      headerName: "Internship Title",
      field: "Title",
      flex: 1.7,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            {row?.Title || "--"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#7A7A7A" }}>
            {row?.BrandName || "--"}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: "Description",
      field: "description",
      flex: 2,
      renderCell: ({ row }) => {
        const fullText =
          row?.SubTitle ||
          stripHtml(row?.JobDescription) ||
          "";

        return (
          <Tooltip
            title={
              <Typography
                sx={{
                  fontSize: "13px",
                  whiteSpace: "pre-line",
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {fullText}
              </Typography>
            }
            placement="bottom"
            arrow
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#757575",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                cursor: "pointer",
                maxWidth: 250,
              }}
            >
              {fullText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Location",
      field: "JobLocation",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "13px", color: "#7A7A7A" }}>
          {row?.JobLocation || "—"}
        </Typography>
      ),
    },
    {
      headerName: "Internship Type",
      field: "TypesEmployment",
      flex: 1.2,
      renderCell: ({ row }) => {
        const type = row?.TypesEmployment
          ? row.TypesEmployment.charAt(0).toUpperCase() +
            row.TypesEmployment.slice(1)
          : "";
        return (
          <Typography sx={{ fontSize: "13px", color: "#7A7A7A" }}>
            {type ? `${type}` : "—"}
          </Typography>
        );
      },
    },
    {
      headerName: "Salary / Stipend",
      field: "StipendAmount",
      flex: 1.2,
      renderCell: ({ row }) => {
        const salaryText = row?.StipendAmount
          ? `₹${Number(row.StipendAmount).toLocaleString("en-IN")}`
          : row?.Stipend || "—";

        return (
          <Typography sx={{ fontWeight: 600, color: "#219653" }}>
            {salaryText}
          </Typography>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#ED1C24",
              color: "#ED1C24",
            }}
            onClick={() =>
              history.push(`/internship/edit-internship/${row?.id}`, {
                eventData: row,
              })
            }
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: "#ED1C24",
              "&:hover": { backgroundColor: "#c3080d" },
            }}
            onClick={() =>
              history.push("/internship/view-internship", {
                eventData: row,
              })
            }
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  // 🔹 Debounce for search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // reset page on new search
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // -------- FETCH DRAFT INTERNSHIPS ----------
  const fetchData = () => {
    setLoader(true);
    const params = {
      offset: page,
      limit: rowsPerPage,
      is_draft: true, // ✅ drafts only
      brand_id: userData?.brand,
    };

    // 🔹 send search to API (debounced)
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    ApiService.get("internships", params)
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          setInternshipDrafts(res?.data?.data || []);
          setCount(res?.data?.count || 0);
        }
      })
      .catch((error) => {
        console.log("internship drafts:", error);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (userData) fetchData();
  }, [userData, page, rowsPerPage, debouncedSearch]); // 🔹 include debouncedSearch

  // after moving search to API, we don't need client-side filter
  const filteredData = internshipDrafts;

  // ---------- NAV ----------
  const openSubmission = () => {
    history.push("/internship/create-internship");
  };

  const handleBackToPublished = () => {
    history.push("/internship");
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) =>
    setRowsPerPage(parseInt(event.target.value, 10));

  return (
    <Box p={4}>
      {/* HEADER */}
      <Box
        display="flex"
        alignItems={{ xs: "none", sm: "center", md: "center" }}
        justifyContent="space-between"
        sx={{
          flexDirection: { xs: "column", sm: "row", md: "row" },
          paddingBottom: { xs: "20px", sm: 0, md: 0 },
        }}
      >
        <ReusableHeadingComponents
          bacHistory={true}
          clickHandlerBack={handleBackToPublished}
          heading={"Internship Drafts"}
          subHeading={"Manage and review all your saved draft internships."}
        />
        <Box sx={{ display: "flex" }}>
          <ReusableButton
            children={"Create internship"}
            variant="contained"
            classNames={"gn-actionbtn"}
            onClick={openSubmission}
          />
        </Box>
      </Box>

      {/* Search + View Toggle */}
      <Box
        mt={1}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder="Search Draft Internship"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: "500px" }, background: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#7A7A7A" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" alignItems="center">
          <Tooltip title="Grid View">
            <IconButton
              onClick={() => setViewMode("grid")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "grid" ? "#7A7A7A" : "transparent",
                color: viewMode === "grid" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Table View">
            <IconButton
              onClick={() => setViewMode("table")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRadius: "0 8px 8px 0",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "table" ? "#7A7A7A" : "transparent",
                color: viewMode === "table" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* GRID OR TABLE VIEW */}
      {viewMode === "grid" ? (
        <Box mb={4}>
          <Grid container spacing={2}>
            {loader ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={i}>
                  <CardSkeletonCollab />
                </Grid>
              ))
            ) : filteredData.length ? (
              filteredData.map((data, index) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                  <InternshipCard
                    internship={data}
                    onEdit={() =>
                      history.push(
                        `/internship/edit-internship/${data?.id}`,
                        { eventData: data }
                      )
                    }
                    onView={() =>
                      history.push("/internship/view-internship", {
                        eventData: data,
                      })
                    }
                  />
                </Grid>
              ))
            ) : (
              <NoResults eventNoData="No Draft Internship Found" />
            )}
          </Grid>
        </Box>
      ) : (
        <Tablecomponent
          loader={loader}
          columns={columns}
          data={filteredData}
          rowsPerPage={rowsPerPage}
          page={page}
          count={count}
          onPageChange={setPage}
          rowsPerPageOptions={[5, 10, 20]}
          onRowsPerPageChange={(e) =>
            setRowsPerPage(parseInt(e.target.value, 10))
          }
        />
      )}

      {/* Pagination – only for grid */}
      {viewMode === "grid" && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-selectLabel": {
              paddingTop: "14px",
            },
            "& .MuiTablePagination-select": {
              display: "inline-flex",
              alignItems: "center",
              paddingY: "0px",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      )}
    </Box>
  );
};

export default InternshipDraftManagement;
